import './style.scss';

import { Button, Icon, Tooltip } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { info, rotateLeft } from '@wordpress/icons';
import classNames from 'classnames';
import React from 'react';

import { ProBadge } from './ProBadge';
import { BaseControlProps } from './types';

export const BaseControl: React.FC< BaseControlProps > = ( {
	id,
	label,
	help,
	className = '',
	children,
	hideLabelFromVision = false,
	noBottomMargin = false,
	inline = false,
	helpAsTooltip = true,
	showResetButton = false,
	onReset,
	resetLabel,
	hasValue = false,
	isPro = false,
} ) => {
	const labelId = id ? `${ id }-label` : undefined;
	const helpId = help && id ? `${ id }-help` : undefined;

	const handleReset = (e: React.MouseEvent< HTMLButtonElement >) => {
		e.preventDefault();
		e.stopPropagation();
		if ( onReset ) {
			onReset();
		}
	};

	return (
		<div
			className={ classNames( className, {
				'redfox-thank-you-base-control': true,
				'redfox-thank-you-base-control--no-bottom-margin':
					noBottomMargin,
				'redfox-thank-you-base-control--inline': inline,
				'redfox-thank-you-base-control--has-reset': showResetButton,
				'redfox-thank-you-base-control--pro': isPro,
			} ) }
		>
			{ label && (
				<div className="redfox-thank-you-base-control__label-wrapper">
					<label
						htmlFor={ id }
						className={ `redfox-thank-you-base-control__label ${
							hideLabelFromVision ? 'screen-reader-text' : ''
						}` }
					>
						<span className="redfox-thank-you-base-control__label-text">
							{ label }
						</span>
						{ isPro && <ProBadge /> }
					</label>
					{ showResetButton && hasValue && ! isPro && (
						<Tooltip
							text={
								resetLabel ??
								__( 'Reset', 'redfox-thank-you' )
							}
						>
							<Button
								className="redfox-thank-you-base-control__reset-button"
								icon={ rotateLeft }
								onClick={ handleReset }
								size="small"
								variant="tertiary"
								aria-label={
									resetLabel ??
									__( 'Reset', 'redfox-thank-you' )
								}
							/>
						</Tooltip>
					) }
					{ help && helpAsTooltip && ! isPro && (
						<Tooltip text={ help }>
							<span className="redfox-thank-you-base-control__help-icon-wrapper">
								<Icon
									icon={ info }
									className="redfox-thank-you-base-control__help-icon"
								/>
							</span>
						</Tooltip>
					) }
				</div>
			) }

			<div
				className="redfox-thank-you-base-control__field"
				style={ isPro ? { opacity: 0.5, pointerEvents: 'none' } : {} }
			>
				{ React.Children.map( children, ( child, index ) => {
					if ( React.isValidElement( child ) && id && index === 0 ) {
						return React.cloneElement(
							child as React.ReactElement,
							{
								id,
								'aria-labelledby': labelId,
								'aria-describedby':
									! helpAsTooltip && ! isPro
										? helpId
										: undefined,
								...child.props,
								disabled: isPro ? true : child.props?.disabled,
							},
						);
					}
					return isPro && React.isValidElement( child )
						? React.cloneElement( child as React.ReactElement, {
							disabled: true,
							...( typeof child.props === 'object'
								? child.props
								: {} ),
						} )
						: child;
				} ) }
			</div>

			{ help && ! helpAsTooltip && ! isPro && (
				<p
					id={ helpId }
					className="redfox-thank-you-base-control__help"
				>
					{ help }
				</p>
			) }
		</div>
	);
};
